import { Injectable, inject, signal } from '@angular/core';
import { GpsTrackingService } from './gps-tracking.service';
import { SyncQueueService } from '../../offline/sync-queue.service';

/**
 * AppLifecycleService — wires app foreground/background transitions to the
 * services that own long-running timers (Req 6.8).
 *
 * On iOS the WKWebView freezes JavaScript execution when the app is backgrounded
 * or the phone is locked, which would otherwise leave the GPS `setInterval` and
 * the sync-queue drain in an inconsistent state (hung timers, half-processed
 * work, battery drain that Apple penalises). We listen to `appStateChange` from
 * `@capacitor/app` and:
 *  - background → suspend GPS capture and pause the sync-queue drain,
 *  - foreground → resume GPS capture (if a session was active) and the drain.
 *
 * This is shared logic and benefits Android too (harmless there). It does NOT
 * alter the queue's retry/priority logic — only when the drain runs.
 */
@Injectable({ providedIn: 'root' })
export class AppLifecycleService {
  private readonly gps = inject(GpsTrackingService);
  private readonly sync = inject(SyncQueueService);

  private initialized = false;

  /**
   * Incremented every time the app returns to the foreground.
   *
   * Consumers use it as a "the OS just handed control back" tick. It exists
   * because a fullscreen system presentation (camera, share sheet, screenshot →
   * another app) leaves WKWebView with stale layout boxes: the `ion-tab-bar`
   * comes back collapsed to height 0, so the footer vanishes and the content
   * stretches (making the header look taller). Ionic/Capacitor do not recompute
   * it on their own — see ionic-team/capacitor#8054 — so `TabsPage` watches this
   * counter and remounts the tab bar. A counter (not a boolean) so consecutive
   * foregrounds each produce a distinct signal change.
   */
  private readonly _foregroundCount = signal(0);
  readonly foregroundCount = this._foregroundCount.asReadonly();

  /**
   * Subscribe to app state changes. Idempotent — safe to call once at startup
   * from the app initializer. Uses a dynamic import of `@capacitor/app` so the
   * web preview (no native bridge) degrades gracefully.
   */
  async initialize(): Promise<void> {
    if (this.initialized) return;
    this.initialized = true;

    try {
      const { App } = await import('@capacitor/app');
      await App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          this.gps.resumeIfTracking();
          this.sync.resumeProcessing();
          // Tell layout-sensitive components the WebView just came back, so they
          // can repair boxes WKWebView left stale (see foregroundCount).
          this._foregroundCount.update((n) => n + 1);
        } else {
          this.gps.suspend();
          this.sync.pauseProcessing();
        }
      });
    } catch (error) {
      // Web preview or missing native bridge — lifecycle wiring is a no-op.
      console.warn('[AppLifecycle] Could not attach appStateChange listener:', error);
    }
  }
}
